import { errorNeedInit, errorNoSiteKey, errorUnexpected, TuringAttr } from './types'
import { getAttrValue, initURL, setElementAttribute } from './misc'
import View from './view/view'
import BotD from '@fpjs-incubator/botd-agent'

export default class Challenge {
  view?: View
  endpoint: string
  debug?: boolean
  siteKey: string
  sessionId?: string
  isBot?: boolean
  enableBotD: boolean
  challengeType?: string
  promise: Promise<string>
  clientForm?: HTMLFormElement

  constructor() {
    const endpoint = getAttrValue(TuringAttr.ServerUrl)
    this.endpoint = endpoint === undefined ? 'https://turing.fpapi.io' : endpoint
    this.debug = getAttrValue(TuringAttr.Debug) !== undefined

    const siteKey = getAttrValue(TuringAttr.DataSiteKey)
    if (siteKey === undefined) {
      throw errorNoSiteKey(`cannot find ${TuringAttr.DataSiteKey} attribute`)
    }
    this.siteKey = siteKey

    this.enableBotD = getAttrValue(TuringAttr.DisableBotD) === undefined
    this.promise = this.createPromise()

    if (this.debug) {
      // eslint-disable-next-line no-console
      console.log(
        `[Turing] constructor - endpoint: ${this.endpoint}, sitekey: ${this.siteKey}, BotD: ${this.enableBotD}`,
      )
    }
  }

  async initialize(): Promise<void> {
    try {
      let botdRequestId = ''

      if (this.enableBotD) {
        const botdPromise = BotD.load({
          publicKey: this.siteKey,
        })
        const botd = await botdPromise
        const result = await botd.detect()
        if ('requestId' in result) {
          botdRequestId = result.requestId
        }
      }

      const body = JSON.stringify({
        public_key: this.siteKey,
        botd_request_id: botdRequestId,
      })

      const resp = await fetch(initURL(this.endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: body,
      })
      const respJSON = await resp.json()
      const type = respJSON.challenge_type as string
      const sessionId = respJSON.session_id as string

      this.challengeType = type
      this.sessionId = sessionId
      this.isBot = type !== 'none' // TODO: more accurate check

      if (this.debug) {
        // eslint-disable-next-line no-console
        console.log(
          `[Turing] initialize - is bot: ${this.isBot}, challenge type: ${this.challengeType}, session: ${this.sessionId}`,
        )
      }

      this.clientForm = this.getClientFormWithSessionId(this.sessionId)
      if (this.isBot) {
        this.view = new View()
        if (this.clientForm !== undefined) {
          this.clientForm.addEventListener(
            'submit',
            (e) => {
              e.preventDefault()
              this.view?.show(this.endpoint, type, this.siteKey, sessionId)
            },
            false,
          )
        }
      }
    } catch (e) {
      throw errorUnexpected(e)
    }
  }

  getClientFormWithSessionId(sessionId: string): HTMLFormElement | undefined {
    if (document.forms[0] === undefined) {
      return undefined
    }
    // TODO: if there are many forms on the page, should we take a form with 'turing-spot' attr?
    const formElement = document.forms[0]
    const hiddenInputElement = document.createElement('input')
    setElementAttribute(hiddenInputElement, {
      type: 'hidden',
      name: 'turing-session-id',
      value: sessionId,
      id: 'session-id',
    })
    formElement.appendChild(hiddenInputElement)
    return formElement
  }

  createPromise(): Promise<string> {
    return new Promise((resolve, reject) => {
      const messageListener = (e: MessageEvent) => {
        if (e.data.solved) {
          if (this.sessionId !== undefined) {
            resolve(this.sessionId)
          } else {
            reject('session id undefined')
          }
          window.removeEventListener('message', messageListener)

          if (this.view !== undefined) {
            this.view.hide()
          }
          if (this.clientForm) {
            this.clientForm.submit()
          }
        }
      }
      window.addEventListener('message', messageListener)
    })
  }

  getPromise(): Promise<string> {
    if (this.isBot !== undefined && !this.isBot) {
      return new Promise((resolve, reject) => {
        if (this.sessionId !== undefined) {
          resolve(this.sessionId)
        } else {
          reject('sessionId undefined')
        }
      })
    } else {
      return this.promise
    }
  }

  render(): void {
    if (this.debug) {
      // eslint-disable-next-line no-console
      console.log(
        `[Turing] render - is bot: ${this.isBot}, challenge type: ${this.challengeType}, session: ${this.sessionId}`,
      )
    }

    if (this.challengeType === undefined || this.sessionId === undefined || this.isBot === undefined) {
      throw errorNeedInit('Initialize framework before call')
    }

    if (this.isBot)
      if (this.view === undefined) {
        throw errorNeedInit('Initialize framework before call')
      } else {
        this.view.show(this.endpoint, this.challengeType, this.siteKey, this.sessionId)
      }
  }
}
